import isPlainObject from 'lodash/isPlainObject';
import noop from 'lodash/noop';
import { call, put, select } from 'redux-saga/effects';
import { createSelector } from 'reselect';

import { AdAnalyticsKeys } from '../../constants';
import * as D from '../../deps';

export const getVideoAdAnalytics = createSelector(
  state => state.videoAdsAnalytics,
  videoAdsAnalytics => videoAdsAnalytics.toJS(),
);

export const getTargetId = createSelector(
  state => state.ads,
  ads => ads.get('targetId'),
);

export function* sendEvent(eventData = {}) {
  yield put(
    D.getInDomain(
      'analytics.actions.logEvents',
      noop,
    )([
      {
        eventName: 'web_audio_video_ad_lifecycle',
        eventData,
      },
    ]),
  );
}

export function* logAudioLifecycleEvent(action, stats = {}) {
  const adBlockerEnabled = yield select(D.getInDomain('application.utils.isAdBlockerDetected'));
  const ctx = yield select(D.getInDomain('audioAds.utils.getAudioAdAnalytics'));
  if (isPlainObject(stats.reason)) {
    stats.reason = JSON.stringify(stats.reason);
  }
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        adBlockerEnabled,
        action,
      },
      D.Constants.Ads.Audio.AudioAnalyticsKeys,
    ),
  );
}

export function* logAdLifecycleEvent(action, stats = {}) {
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...stats,
        action,
      },
      AdAnalyticsKeys,
    ),
  );
}

function convertParamKey(key) {
  return key.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`);
}

function convertParamKeys(params = {}) {
  if (params.status !== undefined) {
    params.status =
      params.status === true
        ? D.Constants.Ads.Video.PlaybackValues.SUCCESS
        : D.Constants.Ads.Video.PlaybackValues.FAILURE;
  }
  return Object.keys(params).reduce((ctx, key) => {
    ctx[convertParamKey(key)] = params[key];
    return ctx;
  }, {});
}

export function normalizeParams(params = {}, keys = {}) {
  const normalizedKeys = removeExtraneousKeys(convertParamKeys(params), keys);
  return normalizedKeys;
}

function removeExtraneousKeys(params = {}, Keys = []) {
  const result = {};
  for (let i = 0; i < Keys.length; ++i) {
    const key = Keys[i];
    let value = params[key];
    if (value !== undefined && value !== null) {
      // all of the known parameters handled by this utility are type string
      if (typeof value !== 'string' && value.toString) value = value.toString();
      if (value) result[key] = value;
    }
  }
  return result;
}

export function* logDfpImpressionStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_VIDEO_DFP_IMPRESSION,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logFetchDfpStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_FETCH_DFP,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logGenerateVastStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_GENERATE_VAST,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logGetAdListStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_GET_AD_LIST,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logImpressionEventStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_TRACKING_EVENT,
        tracking_event_type: D.Constants.Ads.Video.PlaybackValues.IMPRESSION,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logInitPlayerStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_INIT_PLAYER,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logLoadGoogleImaSdk(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_LOAD_GOOGLE_IMA_SDK,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logParseDfpStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_PARSE_DFP,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logTapToVideo(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  stats.station_id = ctx.station_id;
  stats.progress_enforced = true;
  const targetId = yield select(getTargetId);
  // The targetId field is only present for PA video ads (and omitted for non-PA video ads)
  if (targetId) {
    stats.playable_source_id = targetId;
  }
  // the offer_name field should be premium_access/skips/replays depending on the placementId field in context
  switch (ctx.placementId) {
    case D.Constants.Ads.Rewards.RewardConstants.SLOPA:
      stats.offer_name = D.getInDomain('rewards.constants.ReducerKeys.PREMIUM_ACCESS', null);
      break;
    case D.Constants.Ads.Rewards.RewardConstants.FLEX_SKIP:
      stats.offer_name = D.getInDomain('rewards.constants.ReducerKeys.SKIPS', null);
      break;
    case D.Constants.Ads.Rewards.RewardConstants.FLEX_REPLAY:
      stats.offer_name = D.getInDomain('rewards.constants.ReducerKeys.REPLAYS', null);
      break;
  }
  if (stats.event_type === 'skip' || stats.event_type === 'complete') {
    // Clear out the Premium Access video stats so those fields are not logged for non-PA videos
    yield put(D.getInDomain('ads.utils.setPremiumAccessVideoStats', noop)(null, null));
  }
  const eventData = normalizeParams(stats, D.Constants.Ads.Video.TapToVideoKeys);
  yield put(
    D.getInDomain(
      'analytics.actions.logEvents',
      noop,
    )([
      {
        eventName: 'tap_to_video',
        eventData,
      },
    ]),
  );
}

export function* logRegisterVideoAdImpressionStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action:
          D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_REGISTER_VIDEO_AD_IMPRESSION,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logTrackingEventStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_TRACKING_EVENT,
        tracking_event_type: D.Constants.Ads.Video.PlaybackValues.TRACKER,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logVideoEndStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_VIDEO_END,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}

export function* logVideoErrorStats(stats = {}) {
  const ctx = yield select(getVideoAdAnalytics);
  yield call(
    sendEvent,
    normalizeParams(
      {
        ...ctx,
        ...stats,
        action: D.Constants.Ads.Video.VideoLifecycleActions.VAD_LIFECYCLE_VIDEO_ERROR,
      },
      D.Constants.Ads.Video.VideoAnalyticsKeys,
    ),
  );
}
